import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { useDemo } from '../../context/DemoContext'; 
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../services/firebase';

const APPOINTMENT_TYPES = [
  { id: 'NEW_ACTIVATION', label: 'New Line Activation', icon: 'fa-mobile-screen', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'PORT_IN', label: 'Multi-Line Port Transfer', icon: 'fa-layer-group', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'UPGRADE', label: 'Device Upgrade', icon: 'fa-rotate', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { id: 'REPAIR_SERVIFY', label: 'Device Repair / Servify', icon: 'fa-wrench', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'TRADE_IN', label: 'Trade-In Valuation', icon: 'fa-shield-halved', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { id: 'BILL_PAY', label: 'Account Service & Bill Pay', icon: 'fa-globe', color: 'bg-slate-50 text-slate-700 border-slate-200' },
  { id: 'OTHER', label: 'Other / Custom Reason', icon: 'fa-calendar', color: 'bg-slate-100 text-slate-700 border-slate-300' }
];

const FALLBACK_DEVICES = [
  'BYOD (Bring Your Own Device)',
  'Service / Diagnostic Only'
];

export default function GlobalCalendar() {
  const { currentUser } = useAuth();
  const { tenantId, loading: tenantLoading } = useTenant();
  const { isDemoMode } = useDemo();
  
  const activeTenant = isDemoMode ? 'apex_demo' : (currentUser?.tenant_id || tenantId || 'reinhardt_saas');
  
  // Local Data States
  const [availableStores, setAvailableStores] = useState([]);
  const [appointments, setAppointments] = useState([]);
  
  const [selectedStoreId, setSelectedStoreId] = useState('ALL');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeAppointment, setActiveAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  const [isGmailLinked, setIsGmailLinked] = useState(false);

  // Editable Form & Search States
  const [editingAppointmentId, setEditingAppointmentId] = useState(null);
  const [activeDeviceSearchIndex, setActiveDeviceSearchIndex] = useState(null);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    storeLocationId: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    appointmentType: 'NEW_ACTIVATION',
    customReason: '',
    lineCount: 1,
    requestedDevices: [FALLBACK_DEVICES[0]],
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: '11:00',
    durationMinutes: 30,
    assignedRepId: currentUser?.uid || '',
    notes: ''
  });

  const resetForm = () => {
    setFormData({
      storeLocationId: selectedStoreId !== 'ALL' ? selectedStoreId : (availableStores[0]?.id || ''),
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      appointmentType: 'NEW_ACTIVATION',
      customReason: '',
      lineCount: 1,
      requestedDevices: [FALLBACK_DEVICES[0]],
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledTime: '11:00',
      durationMinutes: 30,
      assignedRepId: currentUser?.uid || '',
      notes: ''
    });
    setEditingAppointmentId(null);
  };

  // --- FETCH STORES FOR TENANT ---
  useEffect(() => {
    if (!activeTenant || tenantLoading) return;
    const fetchStores = async () => {
      try {
        const storesRef = collection(db, `clients/${activeTenant}/stores`);
        const q = query(storesRef, where('isActive', '==', true));
        const snapshot = await getDocs(q);
        const storesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAvailableStores(storesList);
        
        if (storesList.length > 0 && formData.storeLocationId === '') {
          setFormData(prev => ({ ...prev, storeLocationId: storesList[0].id }));
        }
      } catch (error) {
        console.error("Failed to load stores:", error);
      }
    };
    fetchStores();
  }, [activeTenant, tenantLoading]);

  // --- LISTEN TO STORE DOCUMENT FOR PUBLIC FLAG ---
  useEffect(() => {
    if (!activeTenant || selectedStoreId === 'ALL') {
      setIsGmailLinked(false);
      return;
    }

    const storeRef = doc(db, `clients/${activeTenant}/stores/${selectedStoreId}`);
    const unsubscribe = onSnapshot(storeRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsGmailLinked(!!data.googleCalendarLinked || !!data.googleRefreshToken || !!data.googleTokens);
      }
    });

    return () => unsubscribe();
  }, [activeTenant, selectedStoreId]);

  // --- LISTEN TO APPOINTMENTS ---
  useEffect(() => {
    if (!activeTenant) return;

    const appointmentsRef = collection(db, `clients/${activeTenant}/appointments`);
    let q = query(appointmentsRef, orderBy('scheduledTime', 'asc'));

    if (selectedStoreId !== 'ALL') {
      q = query(appointmentsRef, where('storeLocationId', '==', selectedStoreId), orderBy('scheduledTime', 'asc'));
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAppointments(loaded);
    }, (error) => {
      console.error("Failed to load appointments:", error);
    });

    return () => unsubscribe();
  }, [activeTenant, selectedStoreId]);

  const handleDeviceChange = (index, device) => {
    const updated = [...formData.requestedDevices];
    updated[index] = device;
    setFormData({ ...formData, requestedDevices: updated });
  };

  const handleLineCountChange = (count) => {
    const current = [...formData.requestedDevices];
    while (current.length < count) current.push(FALLBACK_DEVICES[0]);
    while (current.length > count) current.pop();
    setFormData({ ...formData, lineCount: count, requestedDevices: current });
  };

  // --- OAUTH CONNECTION WITH STATE PAYLOAD ---
  const handleLinkGoogleCalendar = () => {
    if (selectedStoreId === 'ALL') {
      alert("Please select a specific store location to link a Gmail account to.");
      return;
    }

    const clientId = "905738858702-7m54nahv39m7n6ecu91m2c4s8683gisu.apps.googleusercontent.com";
    const redirectUri = `${window.location.origin}/integrations/google/callback`;
    const scope = "https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile";
    
    const stateParam = encodeURIComponent(JSON.stringify({ 
      storeId: selectedStoreId, 
      tenantId: activeTenant 
    }));
    
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=${stateParam}`;
    
    window.location.href = authUrl;
  };

  // --- GOOGLE CALENDAR PULL / SYNC TRIGGER ---
  const handleSyncGoogleCalendar = async () => {
    const targetStore = selectedStoreId !== 'ALL' ? selectedStoreId : availableStores[0]?.id;
    if (!targetStore) {
      alert("Please select a specific store location to sync.");
      return;
    }

    setIsSyncing(true);
    try {
      const functionsInstance = getFunctions();
      const pullCalendar = httpsCallable(functionsInstance, 'pullGoogleCalendarEvents');
      
      const startOfDay = new Date(selectedDate);
      startOfDay.setDate(startOfDay.getDate() - 7);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(selectedDate);
      endOfDay.setDate(endOfDay.getDate() + 30);
      endOfDay.setHours(23, 59, 59, 999);

      await pullCalendar({
        storeId: targetStore,
        tenantId: activeTenant,
        timeMin: startOfDay.toISOString(),
        timeMax: endOfDay.toISOString()
      });
    } catch (err) {
      console.error("Failed to sync from Google:", err);
      alert(err.message || "Failed to sync with Google Calendar.");

      // THE FIX: Self-Healing Protocol
      // If the backend throws an unauthenticated error, wipe the stale flag from the database so the UI resets
      if (err.message && err.message.toLowerCase().includes('unauthenticated')) {
        try {
          await updateDoc(doc(db, `clients/${activeTenant}/stores/${targetStore}`), {
            googleCalendarLinked: false,
            googleRefreshToken: null,
            googleTokens: null
          });
        } catch (e) {
          console.error("Failed to auto-reset stale token flags:", e);
        }
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.customerPhone) return;

    setIsSaving(true);
    const fullIsoDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}:00`).toISOString();

    const payload = {
      tenantId: activeTenant,
      storeLocationId: formData.storeLocationId,
      customerName: formData.customerName,
      customerPhone: formData.customerPhone,
      customerEmail: formData.customerEmail,
      appointmentType: formData.appointmentType,
      customReason: formData.appointmentType === 'OTHER' ? formData.customReason : '',
      lineCount: Number(formData.lineCount),
      requestedDevices: formData.requestedDevices,
      scheduledTime: fullIsoDateTime,
      durationMinutes: Number(formData.durationMinutes),
      assignedRepId: formData.assignedRepId,
      assignedRepName: currentUser?.email || 'Staff Member',
      status: editingAppointmentId ? (activeAppointment?.status || 'SCHEDULED') : 'SCHEDULED',
      notes: formData.notes,
      source: editingAppointmentId ? activeAppointment?.source : 'WALK_IN',
      updatedAt: serverTimestamp()
    };

    if (!editingAppointmentId) {
      payload.createdAt = serverTimestamp();
    }

    try {
      if (editingAppointmentId) {
        await updateDoc(doc(db, `clients/${activeTenant}/appointments`, editingAppointmentId), payload);
        if (activeAppointment?.id === editingAppointmentId) {
          setActiveAppointment({ ...activeAppointment, ...payload, id: editingAppointmentId });
        }
      } else {
        await addDoc(collection(db, `clients/${activeTenant}/appointments`), payload);
      }
      
      try {
        const functionsInstance = getFunctions();
        const pushCalendarEvent = httpsCallable(functionsInstance, 'pushCalendarEvent');
        
        const startDateTime = new Date(fullIsoDateTime);
        const endDateTime = new Date(startDateTime.getTime() + (Number(formData.durationMinutes) * 60000));

        await pushCalendarEvent({
          storeId: formData.storeLocationId,
          tenantId: activeTenant,
          eventId: editingAppointmentId ? activeAppointment?.googleCalendarEventId : undefined,
          eventDetails: {
            summary: `${formData.appointmentType === 'OTHER' ? formData.customReason : formData.appointmentType.replace('_', ' ')} - ${formData.customerName}`,
            description: `Phone: ${formData.customerPhone}\nEmail: ${formData.customerEmail}\nLines: ${formData.lineCount}\nNotes: ${formData.notes || 'No additional notes.'}\n\nBooked via eeKoSystem.`,
            start: {
              dateTime: startDateTime.toISOString(),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            end: {
              dateTime: endDateTime.toISOString(),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            attendees: formData.customerEmail ? [{ email: formData.customerEmail }] : undefined
          }
        });
      } catch (gcalError) {
        console.error("Appointment saved locally, but Google Sync failed:", gcalError);
      }

      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      console.error("Failed to schedule appointment:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateStatus = async (apptId, status) => {
    try {
      await updateDoc(doc(db, `clients/${activeTenant}/appointments`, apptId), { status });
      if (activeAppointment && activeAppointment.id === apptId) {
        setActiveAppointment({ ...activeAppointment, status });
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleDeleteAppointment = async (apptId) => {
    if (window.confirm("Are you sure you want to completely delete this booking?")) {
      try {
        await deleteDoc(doc(db, `clients/${activeTenant}/appointments`, apptId));
        setActiveAppointment(null);
      } catch (err) {
        console.error("Failed to delete appointment:", err);
      }
    }
  };

  const handleEditAppointment = (appt) => {
    const scheduledDateObj = new Date(appt.scheduledTime);
    setFormData({
      storeLocationId: appt.storeLocationId,
      customerName: appt.customerName,
      customerPhone: appt.customerPhone,
      customerEmail: appt.customerEmail || '',
      appointmentType: appt.appointmentType,
      customReason: appt.customReason || '',
      lineCount: appt.lineCount,
      requestedDevices: appt.requestedDevices,
      scheduledDate: scheduledDateObj.toISOString().split('T')[0],
      scheduledTime: scheduledDateObj.toTimeString().slice(0, 5),
      durationMinutes: appt.durationMinutes,
      assignedRepId: appt.assignedRepId || '',
      notes: appt.notes || ''
    });
    setEditingAppointmentId(appt.id);
    setIsModalOpen(true);
  };

  const handlePushToPOSCart = (appt) => {
    handleUpdateStatus(appt.id, 'IN_SERVICE');
    alert(`Appointment ${appt.customerName} checked into service.`);
  };

  const filteredAppointments = appointments.filter(a => {
    const matchesSearch = a.customerName.toLowerCase().includes(filterQuery.toLowerCase()) ||
                          a.customerPhone.includes(filterQuery) ||
                          a.appointmentType.toLowerCase().includes(filterQuery.toLowerCase()) ||
                          (a.customReason && a.customReason.toLowerCase().includes(filterQuery.toLowerCase()));
    
    const apptDate = new Date(a.scheduledTime).toDateString();
    const isSameDate = apptDate === selectedDate.toDateString();

    return matchesSearch && isSameDate;
  });

  if (tenantLoading) return <div className="p-10 flex justify-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-slate-400"></i></div>;

  return (
    <div className="flex h-full w-full bg-slate-100 overflow-hidden font-sans border-t border-slate-200">
      
      {/* LEFT PANE: Calendar & Appointments Timeline */}
      <div className="flex-1 flex flex-col bg-white border-r border-slate-200 overflow-hidden">
        
        {/* Header Bar */}
        <div className="p-6 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-calendar text-[#528265] text-xl"></i>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">Enterprise Appointment Grid</h1>
            </div>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">
              Store Booking Engine & Google Calendar Mirror
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-1.5 rounded-sm">
              <i className="fa-solid fa-store text-slate-400"></i>
              <select 
                value={selectedStoreId} 
                onChange={(e) => setSelectedStoreId(e.target.value)}
                className="bg-transparent text-xs font-bold text-slate-700 outline-none pr-2"
              >
                <option value="ALL">All Network Stores</option>
                {availableStores.map(store => (
                  <option key={store.id} value={store.id}>{store.name} {store.storeNumber ? `(${store.storeNumber})` : ''}</option>
                ))}
              </select>
            </div>

            {/* SYNC / PULL BUTTON */}
            <button 
              onClick={handleSyncGoogleCalendar}
              disabled={isSyncing || !isGmailLinked}
              className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-widest rounded-sm transition-all shadow-sm disabled:opacity-50"
            >
              {isSyncing ? <i className="fa-solid fa-spinner fa-spin text-[#528265]"></i> : <i className="fa-solid fa-rotate text-[#528265]"></i>}
              {isSyncing ? 'Syncing...' : 'Sync Google'}
            </button>

            {/* THE FIX: OAUTH LINK BUTTON (Unlocked & Clickable) */}
            <button 
              onClick={handleLinkGoogleCalendar}
              title={isGmailLinked ? "Click to re-authenticate or change accounts" : "Link Google Calendar"}
              className={`flex items-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-widest rounded-sm transition-all shadow-sm ${
                isGmailLinked 
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300'
                  : 'bg-white border border-slate-200 hover:bg-slate-50 hover:border-[#528265] text-slate-700 hover:text-[#528265]'
              }`}
            >
              {isGmailLinked ? (
                <><i className="fa-solid fa-shield-halved text-emerald-500"></i> Gmail Linked</>
              ) : (
                <><i className="fa-solid fa-globe"></i> Link Store Gmail</>
              )}
            </button>

            {/* MANUAL BOOKING BUTTON */}
            <button 
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#528265] hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors shadow-sm"
            >
              <i className="fa-solid fa-plus"></i> Book Appointment
            </button>
          </div>
        </div>

        {/* Date Selector Strip */}
        <div className="flex items-center justify-between px-6 py-3 bg-slate-50 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => {
                const prev = new Date(selectedDate);
                prev.setDate(prev.getDate() - 1);
                setSelectedDate(prev);
              }}
              className="p-1.5 bg-white border border-slate-200 hover:bg-slate-100 rounded-sm text-slate-600"
            >
              <i className="fa-solid fa-chevron-left text-xs"></i>
            </button>
            <span className="text-sm font-bold text-slate-800 tracking-wide px-3">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <button 
              onClick={() => {
                const next = new Date(selectedDate);
                next.setDate(next.getDate() + 1);
                setSelectedDate(next);
              }}
              className="p-1.5 bg-white border border-slate-200 hover:bg-slate-100 rounded-sm text-slate-600"
            >
              <i className="fa-solid fa-chevron-right text-xs"></i>
            </button>
            <button 
              onClick={() => setSelectedDate(new Date())}
              className="text-xs font-bold text-[#528265] uppercase tracking-wider ml-2 hover:underline"
            >
              Today
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <i className="fa-solid fa-search text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 text-xs"></i>
              <input 
                type="text" 
                placeholder="Search bookings..."
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 bg-white border border-slate-200 text-xs rounded-sm outline-none focus:border-[#528265]"
              />
            </div>
          </div>
        </div>

        {/* Appointments Feed */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {filteredAppointments.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-slate-400 border border-dashed border-slate-200 rounded-sm bg-slate-50/50">
              <i className="fa-solid fa-calendar text-3xl mb-3 opacity-50"></i>
              <p className="text-sm font-bold uppercase tracking-wider text-slate-500">No Appointments Scheduled</p>
              <p className="text-xs text-slate-400 mt-1">Book a manual slot or link your web booking widget.</p>
            </div>
          ) : (
            filteredAppointments.map(appt => {
              const typeConfig = APPOINTMENT_TYPES.find(t => t.id === appt.appointmentType) || APPOINTMENT_TYPES[0];
              const isSelected = activeAppointment?.id === appt.id;
              const formattedTime = new Date(appt.scheduledTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

              return (
                <div 
                  key={appt.id}
                  onClick={() => setActiveAppointment(appt)}
                  className={`p-4 bg-white border rounded-sm transition-all cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm hover:border-[#528265] ${
                    isSelected ? 'border-[#528265] ring-2 ring-[#528265]/10' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm text-center shrink-0 min-w-[70px]">
                      <span className="text-xs font-black text-slate-800 block">{formattedTime}</span>
                      <span className="text-[10px] text-slate-400 font-bold uppercase">{appt.durationMinutes} min</span>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border rounded-sm flex items-center gap-1 ${typeConfig.color}`}>
                          <i className={`fa-solid ${typeConfig.icon}`}></i>
                          {appt.appointmentType === 'OTHER' && appt.customReason ? appt.customReason : typeConfig.label}
                        </span>
                        <span className="text-xs font-bold text-slate-500 font-mono">
                          {appt.lineCount} {appt.lineCount === 1 ? 'Line' : 'Lines'}
                        </span>
                      </div>

                      <h3 className="text-sm font-bold text-slate-900">{appt.customerName}</h3>
                      <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        {appt.customerPhone && <><i className="fa-solid fa-phone text-[10px]"></i> {appt.customerPhone}</>}
                        {appt.assignedRepName && (
                          <span className="text-slate-400">| Rep: <strong className="text-slate-700">{appt.assignedRepName}</strong></span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm ${
                      appt.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' :
                      appt.status === 'IN_SERVICE' ? 'bg-blue-100 text-blue-800' :
                      appt.status === 'CHECKED_IN' ? 'bg-amber-100 text-amber-800' :
                      appt.status === 'CANCELLED' ? 'bg-rose-100 text-rose-800' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {appt.status.replace('_', ' ')}
                    </span>
                    <i className="fa-solid fa-arrow-right text-slate-300"></i>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT PANE: Appointment Detailed Context & Actions */}
      <div className="w-96 bg-white border-l border-slate-200 flex flex-col shrink-0 overflow-hidden">
        {activeAppointment ? (
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            <div className="p-6 border-b border-slate-200 shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Booking Detail</span>
                  <h2 className="text-lg font-bold text-slate-900 mt-1 truncate">{activeAppointment.customerName}</h2>
                </div>
              </div>
              {activeAppointment.customerPhone && (
                <p className="text-xs text-slate-500 font-mono mt-2 flex items-center gap-1.5"><i className="fa-solid fa-phone"></i> {activeAppointment.customerPhone}</p>
              )}
              {activeAppointment.customerEmail && (
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><i className="fa-solid fa-envelope"></i> {activeAppointment.customerEmail}</p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Hardware / Requested Devices</h4>
                <div className="space-y-2">
                  {activeAppointment.requestedDevices.map((dev, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-sm text-xs font-bold text-slate-700">
                      <i className="fa-solid fa-mobile-screen text-[#528265]"></i>
                      <span className="truncate">Line {idx + 1}: {dev}</span>
                    </div>
                  ))}
                </div>
              </div>

              {activeAppointment.notes && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                  <span className="text-[10px] font-bold uppercase text-amber-800 tracking-wider block mb-1">Customer Note</span>
                  <p className="text-xs text-amber-900 leading-relaxed">{activeAppointment.notes}</p>
                </div>
              )}

              <div className="space-y-3 border-t border-slate-200 pt-6">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Status Transition</h4>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => handleUpdateStatus(activeAppointment.id, 'CHECKED_IN')}
                    className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold uppercase rounded-sm transition-colors"
                  >
                    Check-In
                  </button>
                  <button 
                    onClick={() => handleUpdateStatus(activeAppointment.id, 'COMPLETED')}
                    className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold uppercase rounded-sm transition-colors"
                  >
                    Complete
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 shrink-0 space-y-3">
              <button 
                onClick={() => handlePushToPOSCart(activeAppointment)}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#528265] hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors shadow-sm"
              >
                <i className="fa-solid fa-cart-shopping"></i> Load Cart & Transact
              </button>
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <button 
                  onClick={() => handleEditAppointment(activeAppointment)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-colors"
                >
                  <i className="fa-solid fa-pen-to-square"></i> Edit
                </button>
                <button 
                  onClick={() => handleDeleteAppointment(activeAppointment.id)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-rose-50 border border-rose-200 hover:bg-rose-100 text-rose-600 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-colors"
                >
                  <i className="fa-solid fa-trash-can"></i> Delete
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 bg-slate-50/50">
            <i className="fa-solid fa-calendar text-4xl mb-3 opacity-30"></i>
            <p className="text-xs font-bold uppercase tracking-wider">Select an appointment</p>
            <p className="text-[10px] text-slate-400 mt-1 max-w-[200px]">Review device allocations or launch the transaction.</p>
          </div>
        )}
      </div>

      {/* NEW/EDIT APPOINTMENT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[95vh]">
            <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-900 text-white shrink-0">
              <div>
                <h3 className="text-sm font-black uppercase tracking-wide">
                  {editingAppointmentId ? 'Edit Store Appointment' : 'Schedule Store Appointment'}
                </h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">Direct Retail Queue Booking</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white transition-colors">
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreateAppointment} className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Store Location</label>
                  <select 
                    value={formData.storeLocationId}
                    onChange={(e) => setFormData({ ...formData, storeLocationId: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] cursor-pointer shadow-inner"
                  >
                    {availableStores.map(store => (
                      <option key={store.id} value={store.id}>{store.name} {store.storeNumber ? `(${store.storeNumber})` : ''}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Appointment Type</label>
                  <select 
                    value={formData.appointmentType}
                    onChange={(e) => setFormData({ ...formData, appointmentType: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] cursor-pointer shadow-inner"
                  >
                    {APPOINTMENT_TYPES.map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                  
                  {formData.appointmentType === 'OTHER' && (
                    <div className="mt-3">
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-[#528265] mb-1.5">Custom Reason <span className="text-rose-500">*</span></label>
                      <input 
                        type="text"
                        required
                        placeholder="Type exact reason..."
                        value={formData.customReason}
                        onChange={(e) => setFormData({ ...formData, customReason: e.target.value })}
                        className="w-full p-2.5 bg-white border border-[#528265] text-xs font-bold text-slate-800 rounded-sm outline-none shadow-inner"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Customer Name <span className="text-rose-500">*</span></label>
                  <input 
                    type="text"
                    required
                    placeholder="e.g. John Doe"
                    value={formData.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Phone Number <span className="text-rose-500">*</span></label>
                  <input 
                    type="tel"
                    required
                    placeholder="e.g. (360) 555-0199"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Email Address</label>
                <input 
                  type="email"
                  placeholder="For Google Calendar Invites"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner"
                />
              </div>

              <div className="grid grid-cols-3 gap-5">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Date</label>
                  <input 
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Time</label>
                  <input 
                    type="time"
                    value={formData.scheduledTime}
                    onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Line Count</label>
                  <select 
                    value={formData.lineCount}
                    onChange={(e) => handleLineCountChange(Number(e.target.value))}
                    className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner cursor-pointer"
                  >
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'Line' : 'Lines'}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-3 bg-slate-50 p-4 border border-slate-200 rounded-sm">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700">Device Selections by Line</label>
                {formData.requestedDevices.map((dev, i) => (
                  <div key={i} className="flex items-center gap-3 relative">
                    <span className="text-[10px] font-black uppercase text-slate-400 w-12 text-right tracking-widest">Line {i + 1}</span>
                    <div className="flex-1 relative">
                      <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]"></i>
                      <input 
                        type="text"
                        value={activeDeviceSearchIndex === i ? deviceSearchQuery : dev}
                        onFocus={() => { 
                          setActiveDeviceSearchIndex(i); 
                          setDeviceSearchQuery(dev === FALLBACK_DEVICES[0] ? '' : dev); 
                        }}
                        onBlur={() => {
                          setTimeout(() => setActiveDeviceSearchIndex(null), 200);
                        }}
                        onChange={(e) => setDeviceSearchQuery(e.target.value)}
                        placeholder="Search devices..."
                        className="w-full pl-8 pr-3 py-2 bg-white border border-slate-300 text-xs font-bold text-slate-800 rounded-sm outline-none focus:border-[#528265] shadow-inner"
                      />
                      {activeDeviceSearchIndex === i && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-300 shadow-2xl rounded-sm z-50 max-h-48 overflow-y-auto">
                          {FALLBACK_DEVICES.filter(d => d.toLowerCase().includes(deviceSearchQuery.toLowerCase())).map((d, idx) => (
                             <div 
                              key={`fallback-${idx}`}
                              onClick={() => { handleDeviceChange(i, d); setActiveDeviceSearchIndex(null); }}
                              className="p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer text-xs font-bold text-slate-700 transition-colors"
                            >
                              {d}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Notes / Pre-Service Memo</label>
                <textarea 
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Customer port PINs, specific color requests, or repair details..."
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 text-xs text-slate-800 rounded-sm outline-none focus:border-[#528265] resize-none shadow-inner"
                />
              </div>

              <div className="pt-5 mt-2 border-t border-slate-200 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-300 rounded-sm transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-2.5 bg-[#528265] hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-widest rounded-sm transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-check"></i>}
                  {isSaving ? 'Saving...' : (editingAppointmentId ? 'Save Changes' : 'Confirm Booking')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}